import {
  Eye,
  EyeOff,
  Mail,
  Unlock,
  User,
  UserPlus,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { Pressable } from "react-native";
import { type SheetProps, XStack, YStack } from "tamagui";
import { AccountSheetActive } from "../../plans/PlanModel";
import { Body } from "../../ui/Body";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/InputNew";
import { Pill, PillType } from "../../ui/Pill";
import { Sheet } from "../../ui/Sheet";
import { Subtext } from "../../ui/Subtext";
import { Title } from "../../ui/Title";
import { accountRegister } from "../firebaseInit";
import { showConfetti } from "../states.ts/showConfetti";
import { useStores } from "../stores";
import { trackEvent } from "../trackEvent";

export const AccountRegisterSheet = observer(
  ({
    open,
    onRegisterDone,
    onChangeSheet,
    ...props
  }: {
    onRegisterDone?: () => void;
    onChangeSheet?: (sheet: AccountSheetActive) => void;
  } & SheetProps) => {
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<null | Error>(null);
    const [showingPassword, setShowingPassword] = React.useState(false);

    const { sessionStore } = useStores();

    const register = React.useCallback(async () => {
      const e = email.trim();
      if (e) {
        setError(null);
        setLoading(true);
        try {
          const authUser = await accountRegister(e, password);

          await sessionStore.setAuthUser(authUser.user);

          // Make sure to update current super user email
          await sessionStore.superUser?.setEmail(email);

          void showConfetti();
          trackEvent("account_register");

          onRegisterDone?.();
        } catch (e: unknown) {
          if (e instanceof Error) setError(e);
          trackEvent("account_register_failed");
        } finally {
          setLoading(false);
        }
      }
    }, [email, password, onRegisterDone, sessionStore]);

    return (
      <Sheet
        isLoading={loading}
        sheetId="account_register"
        open={open}
        onOpenChange={() => {
          onChangeSheet?.(AccountSheetActive.None);
        }}
        {...props}
        header={
          <YStack gap="$space.medium">
            <XStack gap="$medium" alignItems="center">
              <UserPlus />
              <Title>Register</Title>
            </XStack>
            <Subtext>Be ready to access your Planiras on any device.</Subtext>
          </YStack>
        }
      >
        <YStack gap="$space.medium">
          {error && <Pill type={PillType.Danger}>{error.message}</Pill>}

          <XStack width={"100%"} alignItems="center" gap="$space.medium">
            <Mail />
            <Input
              disabled={loading}
              autoComplete={"email"}
              keyboardType={"email-address"}
              textContentType={"username"}
              inputMode={"email"}
              preventNewLine
              defaultValue={""}
              multiline={false}
              type="border"
              placeholder={"Your email..."}
              onChangeText={(text) => {
                setEmail(text);
              }}
            />
          </XStack>

          <XStack width={"100%"} alignItems="center" gap="$space.medium">
            <Unlock />
            <Input
              disabled={loading}
              secureTextEntry={!showingPassword}
              preventNewLine
              defaultValue={""}
              autoCorrect={false}
              textContentType={"password"}
              multiline={false}
              icon={showingPassword ? Eye : EyeOff}
              onIconPress={() => {
                setShowingPassword(!showingPassword);
              }}
              type="border"
              placeholder={"Your password..."}
              onChangeText={(text) => {
                setPassword(text);
              }}
              onSubmitEditing={register}
            />
          </XStack>

          <Pressable
            onPress={() => {
              onChangeSheet?.(AccountSheetActive.Login);
            }}
          >
            <Body muted>
              Already have an account?{" "}
              <Body muted textDecorationLine="underline">
                Login
              </Body>{" "}
              instead.
            </Body>
          </Pressable>

          <Button isLoading={loading} icon={User} onPress={register}>
            I'm ready!
          </Button>
        </YStack>
      </Sheet>
    );
  },
);
